import { useState } from 'react';
import { X, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { requestNotificationPermission } from '@/services/notifications/oneSignalService';
import { toast } from 'sonner';

interface PushNotificationWizardProps {
  onComplete: () => void;
  onDismiss: () => void;
}

export const PushNotificationWizard = ({ onComplete, onDismiss }: PushNotificationWizardProps) => {
  const [isEnabling, setIsEnabling] = useState(false);

  const handleEnableNotifications = async () => {
    setIsEnabling(true);
    try {
      const success = await requestNotificationPermission();
      
      if (success) {
        toast.success('Push notifications enabled! You\'ll now receive instant updates.');
        onComplete();
      } else {
        toast.error('Failed to enable push notifications. Please check your browser settings.');
      }
    } catch (error) {
      toast.error('An error occurred while enabling notifications.');
    } finally {
      setIsEnabling(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('push-wizard-dismissed', 'true');
    onDismiss();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="max-w-md w-full p-6 relative">
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>

        <div className="flex flex-col items-center text-center space-y-4">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Bell className="h-8 w-8 text-primary" />
          </div>

          <h2 className="text-2xl font-bold">Stay Updated Instantly</h2>
          
          <p className="text-muted-foreground">
            Enable push notifications to get instant alerts about:
          </p>

          <ul className="text-left space-y-2 w-full">
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span>New reagents shared by the community</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span>New reagent requests from researchers</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span>Swap offers and responses</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">✓</span>
              <span>Direct messages</span>
            </li>
          </ul>

          <div className="flex flex-col gap-2 w-full pt-4">
            <Button
              onClick={handleEnableNotifications}
              disabled={isEnabling}
              className="w-full"
            >
              {isEnabling ? 'Enabling...' : 'Enable Push Notifications'}
            </Button>
            
            <Button
              variant="ghost"
              onClick={handleDismiss}
              className="w-full"
            >
              Maybe Later
            </Button>
          </div>

          <p className="text-xs text-muted-foreground pt-2">
            You can change notification preferences anytime in your profile settings
          </p>
        </div>
      </Card>
    </div>
  );
};
